import { Component, Input, OnChanges } from '@angular/core';
import { LocaleService } from 'libs/shared-services/src/lib/locale.service';

import { DashboardDailyResponse } from '../../../../services/restaurant-dashboard/data-store/model/restaurant-dashboard-response';

@Component({
  selector: 'web-foodis-dashboard-performance-chart',
  templateUrl: './dashboard-performance-chart.component.html',
  styleUrls: ['./dashboard-performance-chart.component.scss'],
})
export class DashboardPerformanceChartComponent implements OnChanges {
  @Input() public series: DashboardDailyResponse[] = [];
  @Input() public currencyCode: string = 'EUR';

  public chartPoints: DashboardChartPoint[] = [];
  public valueTicks: DashboardChartTick[] = [];
  public orderLinePoints: string = '';
  public hasActivity: boolean = false;

  public constructor(private readonly localeService: LocaleService) {}

  public ngOnChanges(): void {
    this.buildChart();
  }

  private buildChart(): void {
    const chartWidth = 640;
    const chartHeight = 170;
    const chartTop = 20;
    const groupWidth = this.series.length > 0 ? chartWidth / this.series.length : chartWidth;
    const barWidth = Math.min(42, groupWidth * 0.52);
    const maxValue = Math.max(...this.series.map((point) => point.completedOrderValueCents), 0);
    const maxOrders = Math.max(...this.series.map((point) => point.receivedOrderCount), 0);

    this.hasActivity = maxValue > 0 || maxOrders > 0;
    this.chartPoints = this.series.map((point, index) => {
      const centerX = groupWidth * index + groupWidth / 2;
      const barHeight = maxValue > 0 ? (point.completedOrderValueCents / maxValue) * chartHeight : 0;
      const orderY =
        maxOrders > 0
          ? chartTop + chartHeight - (point.receivedOrderCount / maxOrders) * chartHeight
          : chartTop + chartHeight;

      return {
        ...point,
        barX: centerX - barWidth / 2,
        barY: chartTop + chartHeight - barHeight,
        barWidth,
        barHeight,
        centerX,
        orderY,
        dateLabel:
          this.series.length <= 10 || index % 5 === 0 || index === this.series.length - 1
            ? this.getDateLabel(point.date)
            : '',
        formattedValue: this.formatCurrency(point.completedOrderValueCents),
        showDateLabel:
          this.series.length <= 10 || index % 5 === 0 || index === this.series.length - 1,
      };
    });
    this.orderLinePoints = this.chartPoints
      .map((point) => `${point.centerX},${point.orderY}`)
      .join(' ');
    this.valueTicks = [0, 0.5, 1].map((ratio) => ({
      y: chartTop + chartHeight - ratio * chartHeight,
      label: this.formatCompactCurrency(maxValue * ratio),
    }));
  }

  private getDateLabel(date: string): string {
    const parts = date.split('-');
    return parts.length === 3 ? `${parts[2]}.${parts[1]}` : date;
  }

  private formatCurrency(valueCents: number): string {
    return new Intl.NumberFormat(this.localeService.getLanguageISO2() || undefined, {
      style: 'currency',
      currency: this.currencyCode || 'EUR',
      maximumFractionDigits: 2,
    }).format(valueCents / 100);
  }

  private formatCompactCurrency(valueCents: number): string {
    const currencyValue = valueCents / 100;
    const absoluteValue = Math.abs(currencyValue);
    const compactScale = absoluteValue >= 1_000_000_000
      ? { divisor: 1_000_000_000, suffix: 'B' }
      : absoluteValue >= 1_000_000
        ? { divisor: 1_000_000, suffix: 'M' }
        : absoluteValue >= 1_000
          ? { divisor: 1_000, suffix: 'K' }
          : { divisor: 1, suffix: '' };
    const formattedValue = new Intl.NumberFormat(
      this.localeService.getLanguageISO2() || undefined,
      {
        style: 'currency',
        currency: this.currencyCode || 'EUR',
        maximumFractionDigits: 1,
      }
    ).format(currencyValue / compactScale.divisor);

    return `${formattedValue}${compactScale.suffix}`;
  }
}

export interface DashboardChartPoint extends DashboardDailyResponse {
  barX: number;
  barY: number;
  barWidth: number;
  barHeight: number;
  centerX: number;
  orderY: number;
  dateLabel: string;
  formattedValue: string;
  showDateLabel: boolean;
}

export interface DashboardChartTick {
  y: number;
  label: string;
}
